// src/pages/UseCaseSelector.jsx - COMPLETE CORRECTED VERSION
// ============================================================
// CLIENT ID: Uses AuthContext session.client_id
// Removed hardcoded clientId=1
// ============================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, AlertCircle, Search, ChevronUp, ChevronDown, ChevronsUpDown, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { buildApiUrl } from '@/config/appConfig';
import './UseCaseSelector.css';

const UseCaseSelector = () => {
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  // ============================================================
  // CLIENT ID: Get from AuthContext session
  // ============================================================
  const { session, sessionLoading } = useAuth();
  const clientId = session?.client_id || null;

  useEffect(() => {
    // Only fetch when we have a clientId
    if (clientId) {
      fetchUseCases();
    }
  }, [clientId]);

  const fetchUseCases = async () => {
    // Guard: Don't fetch without clientId
    if (!clientId) {
      setError('No organization selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // FIXED: Use session clientId instead of hardcoded '1'
      const response = await fetch(buildApiUrl(`/api/v2/use-cases-with-scores?clientId=${clientId}`), {
        cache: 'no-store'
      });
      
      if (!response.ok) throw new Error(`Failed to fetch use cases: ${response.status}`);

      const data = await response.json();

      // Handle the response format from use-cases-with-scores endpoint
      const formattedData = (Array.isArray(data) ? data : []).map((uc) => ({
        use_case_id: uc.id,  // Note: endpoint returns 'id' not 'use_case_id'
        use_case_name: uc.name,
        total_score: uc.total_score || 0,
        value_score: uc.value_score || 0,
        implementation_score: uc.implementation_score || 0
      }));

      setUseCases(formattedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load use cases. Please ensure the backend is running.');
      setUseCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (useCaseId) => {
    // Pass clientId to QA dashboard for context
    navigate(`/qa/${useCaseId}?clientId=${clientId}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown size={16} className="sort-icon" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={16} className="sort-icon active" />
      : <ChevronDown size={16} className="sort-icon active" />;
  };

  const sortedUseCases = React.useMemo(() => {
    let sortableItems = [...useCases];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      });
    }
    return sortableItems;
  }, [useCases, sortConfig]);

  const filteredUseCases = sortedUseCases.filter(uc => 
    uc.use_case_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    uc.use_case_id?.toString().includes(searchTerm)
  );

  // ============================================================
  // LOADING STATE: Wait for session
  // ============================================================
  if (sessionLoading) {
    return (
      <div className="selector-loading">
        <RefreshCw size={48} className="qa-spin" />
        <p>Loading your session...</p>
      </div>
    );
  }

  // ============================================================
  // NO CLIENT ID STATE: Show error
  // ============================================================
  if (!clientId) {
    return (
      <div className="selector-error">
        <AlertCircle size={48} />
        <h3>No Organization Selected</h3>
        <p>Your account is not currently associated with an organization.</p>
        <button 
          onClick={() => navigate('/home')}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#18365E',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="selector-loading">
        <Database size={48} className="qa-spin" />
        <p>Loading use cases...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="selector-error">
        <AlertCircle size={48} />
        <p>{error}</p>
        <button 
          onClick={fetchUseCases} 
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="selector-page">
      <div className="selector-header">
        <h1>Select Use Case for QA</h1>
        <p>Click any row to view detailed QA data</p>
      </div>

      <div className="selector-search">
        <Search size={20} />
        <input
          type="text"
          placeholder="Search by ID or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="selector-table-container">
        <table className="selector-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('use_case_id')} className="sortable-header">
                Use Case ID {getSortIcon('use_case_id')}
              </th>
              <th onClick={() => handleSort('use_case_name')} className="sortable-header">
                Use Case Name {getSortIcon('use_case_name')}
              </th>
              <th onClick={() => handleSort('total_score')} className="sortable-header">
                Total Score {getSortIcon('total_score')}
              </th>
              <th onClick={() => handleSort('value_score')} className="sortable-header">
                Value Score {getSortIcon('value_score')}
              </th>
              <th onClick={() => handleSort('implementation_score')} className="sortable-header">
                Implementation Score {getSortIcon('implementation_score')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredUseCases.map((uc) => (
              <tr 
                key={uc.use_case_id} 
                onClick={() => handleRowClick(uc.use_case_id)}
                className="selector-row"
              >
                <td>{uc.use_case_id}</td>
                <td>{uc.use_case_name || `Use Case ${uc.use_case_id}`}</td>
                <td>{uc.total_score?.toFixed(1) || '0.0'}</td>
                <td>{uc.value_score?.toFixed(1) || '0.0'}</td>
                <td>{uc.implementation_score?.toFixed(1) || '0.0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUseCases.length === 0 && (
          <div className="selector-no-results">
            {searchTerm ? `No use cases found matching "${searchTerm}"` : 'No use cases available'}
          </div>
        )}
      </div>
    </div>
  );
};

export default UseCaseSelector;